import React, { useState, useRef, useEffect } from 'react';
import { ShieldCheck, Loader2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { registerPasskey } from '../_services/passkey.service';

const DEVICE_NAME_MIN_LEN = 5;
/** First character A–Z; rest letters, digits, or spaces only; minimum length after trim. */
const DEVICE_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9 ]{4,}$/;

/** For contextual copy when explaining duplicate registration (stores differ by OS). */
function getExistingPasskeyHintCopy() {
    if (typeof navigator === 'undefined') {
        return 'If you already saved a passkey for this account in this browser or your device password manager, use Sign in with Passkey instead of registering again.';
    }
    const ua = navigator.userAgent || '';
    const iPadOsSafari = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    if (/iPad|iPhone|iPod/.test(ua) || iPadOsSafari) {
        return (
            'On iPhone and iPad, Chrome and Safari share the same passkey store (iCloud Keychain). ' +
            'If you already registered a passkey on this device with another browser, you can sign in with it directly — you don\'t need to register again.'
        );
    }
    if (/Android/i.test(ua)) {
        return (
            'On Android, Chrome usually saves passkeys to Google Password Manager. ' +
            'If you already created one on this device or it synced from the same Google account, use Sign in with Passkey — you don\'t need to register again.'
        );
    }
    return 'If you already saved a passkey for this account in this browser or your device password manager, use Sign in with Passkey instead of registering again.';
}

/**
 * @returns {string | null} Error message, or null if valid.
 */
export function validatePasskeyDeviceName(raw) {
    const name = (raw || '').trim();
    if (!name) return 'Please enter a name for this device.';
    if (name.length < DEVICE_NAME_MIN_LEN) {
        return `Device name must be at least ${DEVICE_NAME_MIN_LEN} characters.`;
    }
    if (!DEVICE_NAME_PATTERN.test(name)) {
        if (!/^[A-Za-z]/.test(name)) {
            return 'Device name must start with a letter.';
        }
        return 'Use only letters, numbers, and spaces (special characters are not allowed).';
    }
    return null;
}

/**
 * Device name + register action (shared by Manage Passkeys and onboarding modal).
 */
export default function PasskeyRegisterPanel({
    userId,
    username,
    onSuccess,
    onCancel,
    showExistingDeviceHint = false,
    submitLabel = 'Register Passkey',
    cancelLabel = 'Back',
}) {
    const [deviceName, setDeviceName] = useState('');
    const [registering, setRegistering] = useState(false);
    const [error, setError] = useState('');
    const msgTimerRef = useRef(null);

    const showError = (msg) => {
        clearTimeout(msgTimerRef.current);
        setError(msg);
        msgTimerRef.current = setTimeout(() => setError(''), 5000);
    };

    useEffect(() => () => clearTimeout(msgTimerRef.current), []);

    const handleRegister = async () => {
        const validationError = validatePasskeyDeviceName(deviceName);
        if (validationError) {
            showError(validationError);
            return;
        }
        const trimmed = deviceName.trim();
        setRegistering(true);
        setError('');
        try {
            const result = await registerPasskey(userId, username, trimmed);
            if (result?.statusCode === '1' || result?.StatusCode === '1') {
                setDeviceName('');
                onSuccess?.();
            } else {
                showError(result?.message || result?.Message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            showError(err.message || 'Registration failed. Please try again.');
        } finally {
            setRegistering(false);
        }
    };

    return (
        <div className="space-y-3">
            {showExistingDeviceHint && (
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
                    <span>{getExistingPasskeyHintCopy()}</span>
                </div>
            )}
            <Label htmlFor="passkey-device-name" className="text-sm font-medium">
                Device name
            </Label>
            <Input
                id="passkey-device-name"
                placeholder="My Device"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRegister(); }}
                disabled={registering}
                maxLength={100}
                autoFocus
            />
            <p className="text-xs font-semibold text-primary">
                At least {DEVICE_NAME_MIN_LEN} characters, start with a letter, then letters, numbers, and spaces only (no symbols).
            </p>
            {error && (
                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 rounded-md px-3 py-2">{error}</p>
            )}
            <div className="flex justify-end gap-2">
                <Button
                    onClick={handleRegister}
                    disabled={registering || validatePasskeyDeviceName(deviceName) !== null}
                >
                    {registering ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Registering…</>
                    ) : (
                        <><ShieldCheck className="mr-2 h-4 w-4" />{submitLabel}</>
                    )}
                </Button>
                {onCancel && (
                    <Button variant="secondary" onClick={onCancel} disabled={registering}>
                        {cancelLabel}
                    </Button>
                )}
            </div>
            <p className="text-xs text-muted-foreground">
                Your browser will prompt you to authenticate with your fingerprint, face, or PIN.
            </p>
        </div>
    );
}
